/**
 * Token Manager - Handles access token storage in memory and automatic refresh
 *
 * Key features:
 * - Access token stored in memory only (not localStorage) for security
 * - Refresh token stored in localStorage (sent via X-Client-Type: mobile flow)
 * - Automatic refresh scheduling at 80% of token expiry
 * - Queue system for concurrent requests during refresh
 *
 * NOTE: This module uses raw fetch() for /auth/refresh instead of apiClient because:
 * 1. apiClient middleware depends on tokenManager (circular dependency)
 * 2. apiClient's 401 retry logic would cause infinite loops during refresh
 */

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5002';
const REFRESH_TOKEN_KEY = 'magpiie_refresh_token';

// In-memory token storage
let accessToken: string | null = null;
let refreshTimeout: ReturnType<typeof setTimeout> | null = null;
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

// Auth failure callback - called when token refresh fails
let onAuthFailureCallback: (() => void) | null = null;

/**
 * Set a callback to be invoked when authentication fails (e.g., refresh token expired).
 * This allows the auth system to dispatch logout and clear Redux state.
 * @param callback - Function to call on auth failure
 */
export const setOnAuthFailure = (callback: () => void): void => {
  onAuthFailureCallback = callback;
};

/**
 * Get the current access token from memory
 */
export const getAccessToken = (): string | null => accessToken;

/**
 * Set the access token and schedule automatic refresh
 * @param token - The access token
 * @param expiresIn - Token expiry time in seconds
 */
export const setToken = (token: string, expiresIn: number): void => {
  accessToken = token;
  scheduleRefresh(expiresIn);
};

/**
 * Clear the access token and cancel any pending refresh
 */
export const clearToken = (): void => {
  accessToken = null;
  clearRefreshToken();
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
    refreshTimeout = null;
  }
};

/**
 * Get the refresh token from localStorage
 */
export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

/**
 * Store the refresh token in localStorage
 * @param token - The refresh token
 */
export const setRefreshToken = (token: string): void => {
  localStorage.setItem(REFRESH_TOKEN_KEY, token);
};

/**
 * Clear the refresh token from localStorage
 */
export const clearRefreshToken = (): void => {
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

/**
 * Check if we have a valid access token
 */
export const hasToken = (): boolean => accessToken !== null;

/**
 * Refresh the access token using the stored refresh token
 * Handles concurrent requests by queuing them
 *
 * Uses raw fetch() instead of apiClient to avoid:
 * - Circular dependency (apiClient → tokenManager → apiClient)
 * - Infinite 401 retry loops
 *
 * @returns Promise that resolves to the new access token
 * @throws Error if refresh fails or no refresh token available
 */
export const refresh = async (): Promise<string> => {
  // Check if we have a refresh token
  const storedRefreshToken = getRefreshToken();
  if (!storedRefreshToken) {
    throw new Error('No refresh token available');
  }

  // If already refreshing, queue this request
  if (isRefreshing) {
    return new Promise<string>((resolve, reject) => {
      refreshSubscribers.push((token: string) => {
        if (token) {
          resolve(token);
        } else {
          reject(new Error('Refresh failed'));
        }
      });
    });
  }

  isRefreshing = true;

  try {
    const response = await fetch(`${API_URL}/auth/refresh`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Type': 'mobile',
      },
      body: JSON.stringify({ refresh_token: storedRefreshToken }),
    });

    if (!response.ok) {
      // Refresh failed - token is invalid or expired
      clearToken();
      throw new Error('Token refresh failed');
    }

    const data = await response.json();
    accessToken = data.access_token;
    scheduleRefresh(data.expires_in);

    // Store the new refresh token (token rotation)
    if (data.refresh_token) {
      setRefreshToken(data.refresh_token);
    }

    // Notify all queued subscribers with the new token
    refreshSubscribers.forEach((callback) => callback(data.access_token));
    refreshSubscribers = [];

    return data.access_token;
  } catch (error) {
    // Clear token and notify subscribers of failure
    clearToken();
    refreshSubscribers.forEach((callback) => callback(''));
    refreshSubscribers = [];
    // Notify auth system to dispatch logout and clear Redux state
    if (onAuthFailureCallback) {
      onAuthFailureCallback();
    }
    throw error;
  } finally {
    isRefreshing = false;
  }
};

/**
 * Schedule automatic token refresh at 80% of expiry time
 * @param expiresIn - Token expiry time in seconds
 */
const scheduleRefresh = (expiresIn: number): void => {
  // Clear any existing refresh timeout
  if (refreshTimeout) {
    clearTimeout(refreshTimeout);
  }

  // Schedule refresh at 80% of expiry (e.g., 12 min for 15 min token)
  const refreshTime = expiresIn * 0.8 * 1000;
  refreshTimeout = setTimeout(() => {
    refresh().catch((error) => {
      console.error('Automatic token refresh failed:', error);
    });
  }, refreshTime);
};

/**
 * Check if token refresh is currently in progress
 */
export const isRefreshInProgress = (): boolean => isRefreshing;
